import React, { useState } from "react";
import { useTranslation } from "react-i18next";

/**
 * Bottom sheet with a search box and the full lead list, matching the
 * prototype. Receives the already-filtered-by-callType list from
 * AgentWorkspaceMobile (same filtering AgentWorkspace.jsx already does
 * for desktop) so this component stays purely presentational.
 */
const MobileLeadsSheet = ({ open, onClose, contacts, selectedContactId, onSelect, total }) => {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");

  if (!open) return null;

  const term = search.trim().toLowerCase();
  const visible = term
    ? contacts.filter((c) => {
        const company = (c.company || "").toLowerCase();
        const fullName = `${c.firstName || ""} ${c.lastName || ""}`.toLowerCase();
        const email = (c.email || "").toLowerCase();
        return company.includes(term) || fullName.includes(term) || email.includes(term);
      })
    : contacts;

  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end">
      <div className="absolute inset-0 bg-black/45" onClick={onClose} />
      <div className="relative bg-white rounded-t-2xl px-4 pt-2 pb-4 flex flex-col max-h-[75vh]">
        <div className="w-8 h-1 bg-gray-300 rounded-full mx-auto mb-3" />

        <div className="flex justify-between items-start mb-3">
          <div>
            <p className="text-sm font-extrabold text-primary m-0">
              {t("agent.mobile.allLeads", "Todos los leads")}
            </p>
            <p className="text-[10px] text-gray-400 m-0 mt-0.5">
              {t("agent.mobile.inQueue", "{{count}} en cola", { count: total })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-6 h-6 rounded-full bg-secondaryBg flex items-center justify-center flex-shrink-0"
          >
            <svg viewBox="0 0 24 24" fill="none" width="12" height="12">
              <path d="M6 6l12 12M18 6 6 18" stroke="#445078" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div className="flex items-center gap-1.5 bg-secondaryBg border border-gray-200 rounded-lg px-2.5 py-2 mb-2.5">
          <svg viewBox="0 0 15 15" fill="none" width="14" height="14">
            <path d="M14.6259 12.8228L11.4545 9.66037C12.1097 8.67558 12.4933 7.49646 12.4933 6.22888C12.4933 2.79427 9.69104 0 6.24664 0C2.80224 0 0 2.79427 0 6.22888C0 9.66348 2.80224 12.4578 6.24664 12.4578C7.51783 12.4578 8.70032 12.0753 9.68792 11.4219L12.8593 14.5843C13.3466 15.0708 14.1386 15.0708 14.6259 14.5843C15.1137 14.0978 15.1137 13.3092 14.6259 12.8228ZM1.87399 6.22888C1.87399 3.82453 3.83544 1.86866 6.24664 1.86866C8.65784 1.86866 10.6193 3.82453 10.6193 6.22888C10.6193 8.63323 8.65784 10.5891 6.24664 10.5891C3.83544 10.5891 1.87399 8.63323 1.87399 6.22888Z" fill="#051D42" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("agent.mobile.searchPlaceholder", "Nombre, correo o empresa...")}
            className="flex-1 bg-transparent border-none text-xs text-primary outline-none"
          />
        </div>

        <div className="overflow-y-auto flex-1 flex flex-col gap-1.5">
          {visible.map((c) => {
            const isSelected = c.contactId === selectedContactId;
            return (
              <button
                key={c.contactId}
                type="button"
                onClick={() => {
                  onSelect(c.contactId);
                  onClose();
                }}
                className={`text-left rounded-xl px-3 py-2 flex justify-between items-center gap-2 border ${
                  isSelected ? "bg-lightBlue border-secondary" : "bg-secondaryBg border-transparent"
                }`}
              >
                <div className="min-w-0">
                  <p className="text-[11px] font-bold text-primary m-0 truncate">
                    {c.firstName} {c.lastName}
                  </p>
                  <p className="text-[10px] text-gray-400 m-0 truncate">{c.company || "-"}</p>
                </div>
                <span className="text-[9px] font-extrabold text-tertiary bg-[#FFF1DB] px-1.5 py-0.5 rounded-full flex-shrink-0">
                  {c.attempts || 0}°
                </span>
              </button>
            );
          })}
          {visible.length === 0 && (
            <p className="text-xs text-gray-400 text-center py-6">
              {t("agent.nextCall.noContactModal.desc")}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MobileLeadsSheet;